/* Components */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  createFragmentContainer,
  graphql,
  RelayProp,
  Environment,
  QueryRenderer,
} from 'react-relay';

import { routerShape } from 'found/lib/PropTypes';
import { Flex, Box } from 'grid-styled';
import compose from 'recompose/compose';
import { TopNavMessageBar } from '@onehope/app-shell';

/* Material UI */
import CircularProgress from 'material-ui/CircularProgress';
import FlatButton from 'material-ui/FlatButton';
import IconButton from 'material-ui/IconButton';

/* Material UI SVG */
import CloseIcon from 'material-ui/svg-icons/navigation/close';
import SearchIcon from 'material-ui/svg-icons/action/search';

import { NavbarComponentQuery } from './__generated__/NavbarComponentQuery.graphql';

/* Mutations */
import ShopWithCeoAddMutation from '../../../mutationsModern/User/ShopWithCeoAddMutation';
import ShopWithCustomerDeleteMutation from '../../../mutationsModern/User/ShopWithCustomerDeleteMutation';

/* Helpers */
import {
  urlLogo,
  urlIcons,
  urlDefaultProfile,
  urlProfile,
} from '../../../../server/utils/urlConstants';

import {
  styles,
  buttonFirstStyles,
  buttonStyles,
  buttonFirstStylesWide,
} from './Navbar.styles';
import { resetSegment } from '../../../utils/tracking';
import { withDialogToggle } from '../../../marketplace/components/_common/_materialui/DialogToggle';
import { Dialogs } from '../../../marketplace/components/_common/_materialui/DialogsEnum';
import DialogPadding from '../../../marketplace/components/_common/_materialui/DialogPadding';
import { tablet, mobile } from '../../../utils/deviceWidths';
import { PC_SUBSCRIPTION_ID } from '../Cart/constants';

import { urlCeHome } from '../../../../server/utils/routeConstants';
import reassignLocation from '../../../utils/reassignLocation';

/* Styles */
import theme from '../../../styles/themes/hopeTheme';
import './Navbar.scss';

import { NavbarComponent_viewer } from './__generated__/NavbarComponent_viewer.graphql';
import { NavbarComponent_user } from './__generated__/NavbarComponent_user.graphql';

interface NavbarComponentProps {
  relay: RelayProp;
  viewer?: NavbarComponent_viewer;
  user?: NavbarComponent_user;
  searchOpen: boolean;
  toggleSearch: () => {};
  handleLogout: (fn: () => void) => void;
  // eslint-disable-next-line
  location: string;
  router: routerShape;
  handleCartToggle: () => void;
  handleShopToggle: () => void;
  handleShopClose: () => void;
  handleRouteChange: (
    url: string,
    something?: any,
    somethingelse?: boolean,
  ) => {};
  containerWidth: number;
  isLoggingOut: boolean;
  overlayComponent?: Node;
  handleExitEvent: (callback?: () => void) => void;
  handleCartHotSwap: () => {};
  handleCartClose: () => void;
  handleAuthentication: () => {};
  handleSearchForCEOSAndHosts: () => void;
  handleSearchForProducts: () => void;
  searchEvents: boolean;
  searchProducts: boolean;
  searchCEOS: boolean;
  isHelpHostDialogOpen: boolean;
  handleOpenHelpHostDialog: () => void;
  handleCloseHelpHostDialog: () => void;
  openDialog: Dialogs;
  toggleDialog: (dialog: Dialogs) => void;
}

interface NavbarComponentState {
  isShopWithCeoDialogOpen: boolean;
  ShopWithCeoDialog: any | null;
  HelpHostDialog: any | null;
  // AsyncAutoComplete: any | null;
  SearchBarNav: any | null;
  NavbarDropdown: any | null;
  TastingKitModal: any | null;
}

class NavbarComponent extends Component<
  NavbarComponentProps,
  NavbarComponentState
> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  state = {
    isShopWithCeoDialogOpen: false,
    ShopWithCeoDialog: null,
    TastingKitModal: null,
    HelpHostDialog: null,
    // AsyncAutoComplete: null,
    SearchBarNav: null,
    NavbarDropdown: null,
  };

  componentDidMount() {
    // import('../AutoComplete/AsyncAutoComplete').then(module => {
    //   this.setState({ AsyncAutoComplete: module.default });
    // });
    import('../SearchBarNav/SearchBarNav').then(module => {
      this.setState({ SearchBarNav: module.default });
    });
    import('../ShopWithCeoDialog/ShopWithCeoDialog').then(module => {
      this.setState({ ShopWithCeoDialog: module.default });
    });
    import('./HelpHostDialog').then(module => {
      this.setState({ HelpHostDialog: module.default });
    });
    import('./NavbarDropdown').then(module => {
      this.setState({ NavbarDropdown: module.default });
    });
    import('../TastingKitModal/TastingKitModal').then(module => {
      this.setState({ TastingKitModal: module.default });
    });
  }

  userLogout = () => {
    const { handleLogout, handleRouteChange, location } = this.props;
    const onCompleted = () => {
      if (location.includes('checkout')) {
        handleRouteChange('/');
      }
      resetSegment();
    };
    return handleLogout(onCompleted);
  };

  handleHomeRouteChange = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const { handleRouteChange } = this.props;
    handleRouteChange('/', null, true);
  };

  handleShopRouteChange = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const { handleRouteChange } = this.props;
    handleRouteChange('/shop', null, true);
  };

  handleGiftRouteChange = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const { handleRouteChange } = this.props;
    handleRouteChange('/shop/gifts/gift', 'gift', false);
  };

  handleJoinRouteChange = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const { handleRouteChange } = this.props;
    handleRouteChange('/join');
  };

  handleAboutRouteChange = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const { handleRouteChange } = this.props;
    handleRouteChange('/about-us');
  };

  handleWineRouteChange = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const { handleRouteChange } = this.props;
    handleRouteChange('/shop/wine/wine', 'wine', false);
  };

  handleHostRouteChange = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const { handleRouteChange } = this.props;
    handleRouteChange('/host');
  };

  handlePerksRouteChange = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const { handleRouteChange } = this.props;
    handleRouteChange('/preferred');
  };

  handleVisitRouteChange = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const { handleRouteChange } = this.props;
    handleRouteChange('/visit');
  };

  handleOpenShopWithCeoDialog = () => {
    this.setState({ isShopWithCeoDialogOpen: true });
  };

  handleOpenHostDialog = () => {
    this.props.toggleDialog(Dialogs.EventShop);
  };

  handleCloseShopWithCeoDialog = () => {
    this.setState({ isShopWithCeoDialogOpen: false });
  };

  onRegistrationComplete = () => {
    const { handleCartHotSwap } = this.props;
    handleCartHotSwap();
    this.handleCloseShopWithCeoDialog();
  };

  onRegistrationError = (errors: Object) => {
    console.log('error in register mutation', errors);
  };

  shopWithCeo = (ceoId: string) => {
    const {
      relay: { environment },
    } = this.props;
    const onCompleted = this.onRegistrationComplete;
    const onError = this.onRegistrationError;
    return ShopWithCeoAddMutation.commit({
      ceoId,
      environment,
      onCompleted,
      onError,
    });
  };

  handleSearchForCEOSAndHosts = () => {
    this.props.handleSearchForCEOSAndHosts();
    this.props.toggleSearch();
  };

  resetSearch = () => {
    this.props.handleSearchForProducts();
  };

  removeCustomerCart = () => {
    const {
      relay: { environment },
      user,
    } = this.props;

    ShopWithCustomerDeleteMutation.commit({
      environment,
      onCompleted: () => {
        console.log('customer shopping removed');
        // handleCartHotSwap();
        // if (window.location.pathname === '/checkout') {
        //   window.location.reload();
        // }
        const isCE =
          user &&
          user.permissions &&
          user.permissions.oneCommerce &&
          user.permissions.oneCommerce.ceo &&
          user.permissions.oneCommerce.ceo.access;
        if (isCE) {
          this.goToCeoDashBoard();
        }
      },
      onError: trans => {
        console.log('TRANS error: ', trans);
      },
    });
  };

  goToCeoDashBoard = (e?: React.MouseEvent) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    const { router } = this.context;
    reassignLocation(urlCeHome, router.push);
  };

  isPDP = () => {
    if (window) {
      const { pathname } = window.location;
      return pathname.match('shop/product/');
    }
    return true;
  };

  render() {
    const {
      ShopWithCeoDialog,
      HelpHostDialog,
      // AsyncAutoComplete,
      SearchBarNav,
      NavbarDropdown,
      TastingKitModal,
    } = this.state;
    const {
      viewer,
      handleCartToggle,
      handleShopToggle,
      handleRouteChange,
      isLoggingOut,
      location,
      containerWidth,
      handleAuthentication,
      searchOpen,
      handleCartHotSwap,
      handleExitEvent,
      handleShopClose,
      toggleSearch,
      searchCEOS,
      searchEvents,
      searchProducts,
      isHelpHostDialogOpen,
      handleCloseHelpHostDialog,
      handleCartClose,
      openDialog,
      overlayComponent,
      user,
      flags,
    } = this.props;
    // overlayComponent is currently only in the center. We could need to support other configurations later.
    const hideLogo = containerWidth <= tablet && overlayComponent;
    const hideButtons = !!overlayComponent;

    const { isShopWithCeoDialogOpen } = this.state;
    const cart = user && user.cartV2;
    // if (!user || !user.cart) return <div />;
    const totalNumberOfItems = cart && cart.totalNumberOfItems;
    // const classSearchBar = searchOpen
    //   ? 'header-search displayAll'
    //   : 'header-search displayNone';
    // const classSearchClose = searchOpen
    //   ? 'search-close displayAll'
    //   : 'search-close displayNone';
    const classSearchBar = 'header-search displayNone';
    const classSearchClose = 'search-close displayNone';
    const classHeader = searchOpen ? 'HeaderZHigh' : 'Header';
    const paddingNavBarButtons = (containerWidth - 560) / 2;

    const productCatalogPageV2 = flags && flags.productCatalogPageV2;
    const showHamburger = productCatalogPageV2
      ? !this.isPDP() || (this.isPDP && containerWidth <= mobile)
      : true;

    const cartHasPCSubscription = false;
    const cartItemCount = cartHasPCSubscription
      ? (totalNumberOfItems || 0) - 1
      : totalNumberOfItems || 0;

    let shoppingWithCeoUser;
    let conciergeUser;
    if (user) {
      ({ shoppingWithCeoUser, conciergeUser } = user);
    }
    return (
      <header role="banner" className="header">
        <div className={classHeader} id="HeaderAutoHide">
          <Flex>
            <Box
              style={
                containerWidth < 475
                  ? {
                      padding: '47px 16px 0 16px',
                      marginRight: -24,
                    }
                  : {
                      padding: '24px 16px 0 16px',
                      marginRight: -24,
                    }
              }
              className={classSearchClose}
            >
              <IconButton
                iconStyle={styles.searchCloseIcon}
                style={styles.searchClose}
                onClick={toggleSearch}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {overlayComponent && overlayComponent}
            {TastingKitModal && (
              // @ts-ignore JSX element type 'ShopWithCeoDialog' does not have any construct or call signatures.
              <TastingKitModal user={user} />
            )}
            {shoppingWithCeoUser &&
              isShopWithCeoDialogOpen &&
              ShopWithCeoDialog && (
                // @ts-ignore JSX element type 'ShopWithCeoDialog' does not have any construct or call signatures.
                <ShopWithCeoDialog
                  user={user}
                  ceoId={shoppingWithCeoUser.publicId}
                  containerWidth={containerWidth}
                  handleCartHotSwap={handleCartHotSwap}
                  handleExitEvent={handleExitEvent}
                  handleRouteChange={handleRouteChange}
                  handleAuthentication={handleAuthentication}
                  handleShopWithCeo={this.shopWithCeo}
                  handleCloseShopWithCeoDialog={
                    this.handleCloseShopWithCeoDialog
                  }
                  isShopWithCeoDialogOpen={isShopWithCeoDialogOpen}
                />
              )}
            {cart && cart.eventId && HelpHostDialog && (
              // @ts-ignore JSX element type 'AsyncAutoComplete' does not have any construct or call signatures.
              <HelpHostDialog
                viewer={viewer}
                eventId={cart && cart.eventId}
                containerWidth={containerWidth}
                handleExitEvent={handleExitEvent}
                handleCartClose={handleCartClose}
                handleRouteChange={handleRouteChange}
                handleCartHotSwap={handleCartHotSwap}
                isHelpHostDialogOpen={isHelpHostDialogOpen}
                handleAuthentication={handleAuthentication}
                handleCloseHelpHostDialog={handleCloseHelpHostDialog}
              />
            )}
            {searchOpen && SearchBarNav && (
              // @ts-ignore JSX element type 'SearchBarNav' does not have any construct or call signatures.
              <SearchBarNav
                user={user}
                searchCEOS={searchCEOS}
                searchEvents={false}
                searchProducts={searchProducts}
                toggleSearch={toggleSearch}
                resetSearch={this.resetSearch}
                handleShopClose={handleShopClose}
                handleCartHotSwap={handleCartHotSwap}
              />
              // <Box className={classSearchBar}>
              //   {/*
              //       // @ts-ignore JSX element type 'AsyncAutoComplete' does not have any construct or call signatures. */}
              //   <AsyncAutoComplete
              //     user={user}
              //     searchCEOS={searchCEOS}
              //     searchEvents={searchEvents}
              //     searchProducts={searchProducts}
              //     toggleSearch={toggleSearch}
              //     resetSearch={this.resetSearch}
              //     containerWidth={containerWidth}
              //     handleShopClose={handleShopClose}
              //     handleRouteChange={handleRouteChange}
              //     handleCartHotSwap={handleCartHotSwap}
              //     handleExitEvent={handleExitEvent}
              //     handleAuthentication={handleAuthentication}
              //   />
              // </Box>
            )}
          </Flex>
          <DialogPadding openDialog={openDialog}>
            {user && (
              <TopNavMessageBar
                openHelpHostDialog={this.handleOpenHostDialog}
                handleOpenShopWithCeoDialog={this.handleOpenShopWithCeoDialog}
                user={user}
                removeCustomerCart={this.removeCustomerCart}
                toggleSearch={this.handleSearchForCEOSAndHosts}
                goToCeoDashBoard={this.goToCeoDashBoard}
                urlProfile={urlProfile}
                urlDefaultProfile={urlDefaultProfile}
              />
            )}
          </DialogPadding>
          <Flex
            className="nav-primary"
            align="center"
            justify={containerWidth <= tablet ? 'center' : 'space-between'}
            style={{ color: theme.hopeBlack }}
          >
            <Flex column={false}>
              <Flex align="center" className="NavBar__hamburgerCont">
                {showHamburger && (
                  <button
                    type="button"
                    className="NavBar__hamburgerButton"
                    onClick={handleShopToggle}
                  >
                    <img alt="shop-nav" src={`${urlIcons}burger.svg`} />
                  </button>
                )}
              </Flex>
              <Flex
                align="center"
                justify="center"
                className="onehope-logo-container"
              >
                {!hideLogo && (
                  <a
                    href="/"
                    onClick={this.handleHomeRouteChange}
                    className="header-logo"
                  >
                    <img
                      className="onehope-logo-img"
                      alt="onehope-logo"
                      src={`${urlLogo}onehope-new-logo-navigation.svg`}
                    />
                  </a>
                )}
              </Flex>
            </Flex>

            {!hideButtons && (
              <Box
                className="Navbar__buttons"
                style={{
                  margin: `0 ${paddingNavBarButtons}px`,
                }}
              >
                <Flex align="center">
                  {containerWidth > tablet && (
                    <Box>
                      <IconButton
                        iconStyle={styles.searchIcon}
                        style={styles.search}
                        data-cypress="searchIcon"
                        onClick={toggleSearch}
                      >
                        <SearchIcon />
                      </IconButton>
                    </Box>
                  )}
                  {containerWidth > tablet && (
                    <Box>
                      <FlatButton
                        data-cypress="WineShopButton"
                        style={buttonFirstStyles}
                        labelStyle={buttonStyles}
                        label="Shop"
                        onClick={this.handleWineRouteChange}
                      />
                    </Box>
                  )}
                  {containerWidth > tablet && (
                    <Box>
                      <FlatButton
                        style={buttonFirstStyles}
                        labelStyle={buttonStyles}
                        label="Perks"
                        onClick={this.handlePerksRouteChange}
                      />
                    </Box>
                  )}
                  {containerWidth > tablet && (
                    <Box>
                      <FlatButton
                        style={buttonFirstStyles}
                        labelStyle={buttonStyles}
                        label="Visit"
                        onClick={this.handleVisitRouteChange}
                      />
                    </Box>
                  )}
                  {containerWidth > tablet && (
                    <Box>
                      <FlatButton
                        style={buttonFirstStyles}
                        labelStyle={buttonStyles}
                        label="Join"
                        onClick={this.handleJoinRouteChange}
                      />
                    </Box>
                  )}
                  {containerWidth > tablet && (
                    <Box>
                      <FlatButton
                        style={buttonFirstStyles}
                        labelStyle={buttonStyles}
                        label="Host"
                        onClick={this.handleHostRouteChange}
                      />
                    </Box>
                  )}
                  {containerWidth > tablet && (
                    <Box>
                      <FlatButton
                        style={buttonFirstStyles}
                        labelStyle={buttonStyles}
                        label="About"
                        onClick={this.handleAboutRouteChange}
                      />
                    </Box>
                  )}
                </Flex>
              </Box>
            )}

            <DialogPadding openDialog={openDialog}>
              <Box className="account-section">
                <Flex align="center">
                  <Box pr={2}>
                    {isLoggingOut && (
                      <CircularProgress
                        color="#784cab"
                        size={27.75}
                        thickness={1.75}
                      />
                    )}
                  </Box>
                  {containerWidth <= tablet && (
                    <Box
                      className={
                        location === '/checkout'
                          ? 'NavBar__searchButtonCheckout'
                          : 'NavBar__searchButton'
                      }
                    >
                      <IconButton
                        iconStyle={styles.searchIcon}
                        style={
                          containerWidth > tablet
                            ? styles.search
                            : styles.mobileSearch
                        }
                        onClick={toggleSearch}
                      >
                        <SearchIcon />
                      </IconButton>
                    </Box>
                  )}
                  {containerWidth > tablet && NavbarDropdown && (
                    <NavbarDropdown
                      containerWidth={containerWidth}
                      user={user}
                      flags={flags}
                      location={this.props.location}
                      handleRouteChange={handleRouteChange}
                      handleLogout={this.props.handleLogout}
                    />
                  )}

                  {location !== '/checkout' && (
                    <Box
                      className="NavBar__cartContainer"
                      onClick={handleCartToggle}
                    >
                      <button
                        type="button"
                        className="cartRightNav-button"
                        data-cypress="cartRightNav-button"
                        id="cartRightNav-button"
                      >
                        <Box
                          className="cartRightNav-buttonBackground"
                          style={{
                            backgroundImage: `url(${urlIcons}bag.svg)`,
                          }}
                        >
                          {cartItemCount}
                        </Box>
                      </button>
                    </Box>
                  )}
                </Flex>
              </Box>
            </DialogPadding>
          </Flex>
        </div>
      </header>
    );
  }
}

const NavBarComposed = compose(
  // pure,
  withDialogToggle,
)(NavbarComponent);

const NavBarFragmentComponent = createFragmentContainer<NavbarComponentProps>(
  NavBarComposed,
  {
    user: graphql`
      fragment NavbarComponent_user on User {
        ...SearchBarNav_user
        ...ShopWithCeoDialog_user
        ...NavbarDropdown_user
        id
        publicId
        userId
        role
        email
        firstName
        profile {
          id
          image
        }
        permissions {
          id
          oneCommerce {
            id
            ceo {
              access
            }
            preferredCustomer {
              access
            }
          }
        }
        preferredCustomerCeoAccountId
        shoppingWithCeoUser {
          id
          publicId

          userId
          firstName
          lastName
          profile {
            id
            image
          }
        }
        conciergeUser {
          id
          userId
          firstName
          lastName
          organizationName
        }
        accountRole {
          accountRoleTitle
        }
        cartV2 {
          id
          totalNumberOfItems
          eventId
          eventHostFirstName
          event {
            id
            hostAccountId
            accountId
          }
        }
      }
    `,
    viewer: graphql`
      fragment NavbarComponent_viewer on Viewer {
        id
        ...HelpHostDialog_viewer
      }
    `,
  },
);

type NavbarRendererProps = NavbarComponentProps & {
  environment: Environment;
};

const Renderer = ({ environment, ...props }: NavbarRendererProps) => {
  return (
    <QueryRenderer<NavbarComponentQuery>
      variables={{}}
      dataFrom="STORE_THEN_NETWORK"
      environment={environment}
      query={graphql`
        query NavbarComponentQuery {
          viewer {
            id
            id
            ...NavbarComponent_viewer
            user {
              id
              id
              ...NavbarComponent_user
            }
          }
        }
      `}
      render={({ error, props: propsFromRender }) => {
        if (error) {
          return <div />;
        }
        return (
          <NavBarFragmentComponent
            {...props}
            viewer={propsFromRender && propsFromRender.viewer}
            user={
              propsFromRender &&
              propsFromRender.viewer &&
              propsFromRender.viewer.user
            }
          />
        );
      }}
    />
  );
};

export default Renderer;
